import type { TableAction, BulkAction } from "@shared/types/data-table"
import type { Order } from "@features/in-stores/types/order.types"
import { Eye, Package, RotateCcw, X, MoreHorizontal } from "lucide-react"

type OrderActionHandlers = {
  onView?: (order: Order) => void
  onPreview?: (order: Order) => void
  onEdit?: (order: Order) => void
  onDuplicate?: (order: Order) => void
  onCancel?: (order: Order) => void
  onReturn?: (order: Order) => void
  onMarkNotReceived?: (order: Order) => void
  onFulfill?: (orderIds: string[]) => void
  onBulkCancel?: (orderIds: string[]) => void
  onBulkReturn?: (orderIds: string[]) => void
  onMore?: (orderIds: string[]) => void
}

export const getOrderBulkActions = (handlers: OrderActionHandlers = {}): BulkAction[] => [
  {
    key: "fulfill",
    label: "Fulfill Orders",
    icon: <Package className="h-4 w-4" />,
    onClick: (ids) => handlers.onFulfill?.(ids),
    disabled: (ids) => ids.length === 0,
  },
  {
    key: "cancel",
    label: "Cancel Orders",
    icon: <X className="h-4 w-4" />,
    variant: "destructive",
    onClick: (ids) => handlers.onBulkCancel?.(ids),
    disabled: (ids) => ids.length === 0,
  },
  {
    key: "return",
    label: "Return Orders",
    icon: <RotateCcw className="h-4 w-4" />,
    onClick: (ids) => handlers.onBulkReturn?.(ids),
    disabled: (ids) => ids.length === 0,
  },
  {
    key: "more",
    label: "More Actions",
    icon: <MoreHorizontal className="h-4 w-4" />,
    onClick: (ids) => handlers.onMore?.(ids),
  },
]

// Row actions for tables - quick access operations
export const getOrderRowActions = (handlers: OrderActionHandlers = {}): TableAction<Order>[] => [
  {
    key: "preview",
    label: "Preview",
    icon: <Eye className="h-4 w-4" />,
    onClick: (order) => handlers.onPreview?.(order),
  },
  {
    key: "duplicate",
    label: "Duplicate Order",
    onClick: (order) => handlers.onDuplicate?.(order),
  },
  {
    key: "cancel",
    label: "Cancel Order",
    onClick: (order) => handlers.onCancel?.(order),
    condition: (order) => order.status === "pending" || order.status === "confirmed",
  },
  {
    key: "return",
    label: "Return Order",
    onClick: (order) => handlers.onReturn?.(order),
    condition: (order) => order.status === "fulfilled",
  },
]

// Panel actions for detail pages - comprehensive operations
export const getOrderPanelActions = (handlers: OrderActionHandlers = {}): TableAction<Order>[] => [
  {
    key: "edit",
    label: "Edit Order",
    onClick: (order) => handlers.onEdit?.(order),
  },
  {
    key: "duplicate",
    label: "Duplicate Order",
    onClick: (order) => handlers.onDuplicate?.(order),
  },
  {
    key: "cancel",
    label: "Cancel Order",
    onClick: (order) => handlers.onCancel?.(order),
    variant: "destructive" as const,
  },
  {
    key: "return",
    label: "Return Order",
    onClick: (order) => handlers.onReturn?.(order),
  },
  {
    key: "mark-not-received",
    label: "Mark not received",
    onClick: (order) => handlers.onMarkNotReceived?.(order),
  },
]
